import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../../styles/theme';
import API from '../../api';

// ── Option maps (value → i18n key) ───────────────────────────────────────────
const EXPERIENCE_OPTIONS = [
  { value: 'none',         key: 'kyc.mifid.exp_none' },
  { value: 'basic',        key: 'kyc.mifid.exp_basic' },
  { value: 'intermediate', key: 'kyc.mifid.exp_intermediate' },
  { value: 'advanced',     key: 'kyc.mifid.exp_advanced' },
];
const KNOWLEDGE_OPTIONS = [
  { value: 'limited',   key: 'kyc.mifid.know_limited' },
  { value: 'good',      key: 'kyc.mifid.know_good' },
  { value: 'excellent', key: 'kyc.mifid.know_excellent' },
];
const FREQUENCY_OPTIONS = [
  { value: 'never',        key: 'kyc.mifid.freq_never' },
  { value: 'rarely',       key: 'kyc.mifid.freq_rarely' },
  { value: 'occasionally', key: 'kyc.mifid.freq_occasionally' },
  { value: 'frequently',   key: 'kyc.mifid.freq_frequently' },
];
const INCOME_OPTIONS = [
  { value: 'under_20k', key: 'kyc.mifid.income_1' },
  { value: '20k_50k',   key: 'kyc.mifid.income_2' },
  { value: '50k_100k',  key: 'kyc.mifid.income_3' },
  { value: 'over_100k', key: 'kyc.mifid.income_4' },
];
const ASSETS_OPTIONS = [
  { value: 'under_50k',  key: 'kyc.mifid.assets_1' },
  { value: '50k_200k',   key: 'kyc.mifid.assets_2' },
  { value: '200k_500k',  key: 'kyc.mifid.assets_3' },
  { value: 'over_500k',  key: 'kyc.mifid.assets_4' },
];
const INVEST_OPTIONS = [
  { value: 'under_5k', key: 'kyc.mifid.invest_1' },
  { value: '5k_20k',   key: 'kyc.mifid.invest_2' },
  { value: '20k_50k',  key: 'kyc.mifid.invest_3' },
  { value: 'over_50k', key: 'kyc.mifid.invest_4' },
];
const GOAL_OPTIONS = [
  { value: 'capital_preservation', key: 'kyc.mifid.goal_1' },
  { value: 'income',               key: 'kyc.mifid.goal_2' },
  { value: 'growth',               key: 'kyc.mifid.goal_3' },
  { value: 'speculation',          key: 'kyc.mifid.goal_4' },
];
const HORIZON_OPTIONS = [
  { value: 'short',     key: 'kyc.mifid.horizon_1' },
  { value: 'medium',    key: 'kyc.mifid.horizon_2' },
  { value: 'long',      key: 'kyc.mifid.horizon_3' },
  { value: 'very_long', key: 'kyc.mifid.horizon_4' },
];
const RISK_OPTIONS = [
  { value: 'very_low', key: 'kyc.mifid.risk_1' },
  { value: 'low',      key: 'kyc.mifid.risk_2' },
  { value: 'medium',   key: 'kyc.mifid.risk_3' },
  { value: 'high',     key: 'kyc.mifid.risk_4' },
];

// field → { questionKey, options }
const FIELDS = [
  { field: 'investment_experience', questionKey: 'kyc.mifid.q_experience', formKey: 'investmentExperience', options: EXPERIENCE_OPTIONS },
  { field: 'financial_knowledge',   questionKey: 'kyc.mifid.q_knowledge',  formKey: 'financialKnowledge',   options: KNOWLEDGE_OPTIONS },
  { field: 'trading_frequency',     questionKey: 'kyc.mifid.q_frequency',  formKey: 'tradingFrequency',     options: FREQUENCY_OPTIONS },
  { field: 'annual_income',         questionKey: 'kyc.mifid.q_income',     formKey: 'annualIncome',         options: INCOME_OPTIONS },
  { field: 'net_assets',            questionKey: 'kyc.mifid.q_assets',     formKey: 'netAssets',            options: ASSETS_OPTIONS },
  { field: 'investment_amount',     questionKey: 'kyc.mifid.q_invest_amount', formKey: 'investmentAmount',  options: INVEST_OPTIONS },
  { field: 'investment_goal',       questionKey: 'kyc.mifid.q_goal',       formKey: 'investmentGoal',       options: GOAL_OPTIONS },
  { field: 'investment_horizon',    questionKey: 'kyc.mifid.q_horizon',    formKey: 'investmentHorizon',    options: HORIZON_OPTIONS },
  { field: 'risk_tolerance',        questionKey: 'kyc.mifid.q_risk',       formKey: 'riskTolerance',        options: RISK_OPTIONS },
];

// ── Styles ────────────────────────────────────────────────────────────────────
const radioLabelBase = {
  display: 'block',
  padding: UI.spacing.md,
  backgroundColor: '#222',
  borderRadius: UI.borderRadius.sm,
  marginBottom: UI.spacing.sm,
  cursor: 'pointer',
  border: '2px solid transparent',
};

// ── Component ─────────────────────────────────────────────────────────────────
const MifidSettings = ({ userId }) => {
  const { t } = useTranslation();
  const [mifid, setMifid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    API.get(`/users/${userId}/mifid`)
      .then(res => {
        setMifid(res.data.mifid);
        if (res.data.mifid) {
          const m = res.data.mifid;
          setFormData({
            investmentExperience: m.investment_experience || '',
            financialKnowledge:   m.financial_knowledge   || '',
            tradingFrequency:     m.trading_frequency     || '',
            annualIncome:         m.annual_income         || '',
            netAssets:            m.net_assets            || '',
            investmentAmount:     m.investment_amount     || '',
            investmentGoal:       m.investment_goal       || '',
            investmentHorizon:    m.investment_horizon    || '',
            riskTolerance:        m.risk_tolerance        || '',
          });
        } else {
          setFormData({ investmentExperience: '', financialKnowledge: '', tradingFrequency: '', annualIncome: '', netAssets: '', investmentAmount: '', investmentGoal: '', investmentHorizon: '', riskTolerance: '' });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const labelFor = (options, value) => {
    const opt = options.find(o => o.value === value);
    return opt ? t(opt.key) : value || <span style={{ color: '#555' }}>{t('settings.not_set')}</span>;
  };

  const handleChange = (formKey, value) => {
    setFormData(prev => ({ ...prev, [formKey]: value }));
  };

  const handleSave = async () => {
    const allFilled = FIELDS.every(f => formData[f.formKey]);
    if (!allFilled) { setError(t('kyc.mifid.error_fields')); return; }
    setSaving(true);
    setError('');
    try {
      await API.post('/kyc/mifid', { user_id: userId, ...formData });
      // Aggiorna visualizzazione
      setMifid({
        investment_experience: formData.investmentExperience,
        financial_knowledge:   formData.financialKnowledge,
        trading_frequency:     formData.tradingFrequency,
        annual_income:         formData.annualIncome,
        net_assets:            formData.netAssets,
        investment_amount:     formData.investmentAmount,
        investment_goal:       formData.investmentGoal,
        investment_horizon:    formData.investmentHorizon,
        risk_tolerance:        formData.riskTolerance,
        completed_at:          new Date().toISOString().slice(0, 19).replace('T', ' '),
      });
      setEditing(false);
      setSuccess(t('settings.mifid_save_success'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('settings.mifid_save_error'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return null;

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundCard, borderRadius: UI.borderRadius.md, padding: UI.spacing.xl, marginBottom: UI.spacing.xl, boxShadow: UI.shadow.card }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: UI.spacing.lg, paddingBottom: UI.spacing.sm, borderBottom: '1px solid #333' }}>
        <h3 style={{ color: SEMANTIC.actionBuy, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, margin: 0 }}>
          {t('settings.section_mifid')}
        </h3>
        {!editing && (
          <button
            onClick={() => { setEditing(true); setError(''); setSuccess(''); }}
            style={{ background: 'transparent', border: `1px solid ${SEMANTIC.actionBuy}`, color: SEMANTIC.actionBuy, borderRadius: UI.borderRadius.sm, padding: `6px 14px`, fontSize: UI.fontSize.sm, cursor: 'pointer' }}
          >
            <i className="bi bi-pencil" style={{ marginRight: '6px' }} />
            {t('settings.mifid_edit_btn')}
          </button>
        )}
      </div>

      {success && (
        <div style={{ backgroundColor: 'rgba(0,255,136,0.1)', border: `1px solid ${SEMANTIC.actionBuy}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.lg, color: SEMANTIC.actionBuy, textAlign: 'center' }}>
          {success}
        </div>
      )}

      {/* READ-ONLY VIEW */}
      {!editing && (
        <>
          {!mifid ? (
            <p style={{ color: SEMANTIC.textSecondary }}>{t('settings.mifid_no_data')}</p>
          ) : (
            <>
              <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.lg }}>
                {t('settings.mifid_completed_on', { date: mifid.completed_at })}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${UI.spacing.sm} ${UI.spacing.lg}` }}>
                {FIELDS.map(({ field, questionKey, options }) => (
                  <div key={field} style={{ borderBottom: '1px solid #222', paddingBottom: UI.spacing.sm }}>
                    <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, margin: 0 }}>{t(questionKey).replace(' *', '')}</p>
                    <p style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.md, margin: 0, fontWeight: UI.fontWeight.bold }}>
                      {labelFor(options, mifid[field])}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* EDIT FORM */}
      {editing && (
        <div>
          {error && (
            <div style={{ backgroundColor: 'rgba(255,77,77,0.1)', border: `2px solid ${SEMANTIC.actionSell}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.lg, color: SEMANTIC.actionSell, textAlign: 'center' }}>
              {error}
            </div>
          )}

          {FIELDS.map(({ formKey, questionKey, options }) => (
            <div key={formKey} style={{ backgroundColor: '#1a1a1a', borderRadius: UI.borderRadius.sm, padding: UI.spacing.lg, marginBottom: UI.spacing.lg }}>
              <p style={{ color: SEMANTIC.textPrimary, fontWeight: UI.fontWeight.bold, marginBottom: UI.spacing.md }}>{t(questionKey)}</p>
              {options.map(opt => {
                const selected = formData[formKey] === opt.value;
                return (
                  <label key={opt.value} style={{ ...radioLabelBase, border: `2px solid ${selected ? SEMANTIC.actionBuy : 'transparent'}`, backgroundColor: selected ? 'rgba(0,255,136,0.1)' : '#222' }}>
                    <input
                      type="radio"
                      name={formKey}
                      value={opt.value}
                      checked={selected}
                      onChange={() => handleChange(formKey, opt.value)}
                      style={{ marginRight: UI.spacing.sm, accentColor: SEMANTIC.actionBuy }}
                    />
                    <span style={{ color: SEMANTIC.textPrimary }}>{t(opt.key)}</span>
                  </label>
                );
              })}
            </div>
          ))}

          <div style={{ display: 'flex', gap: UI.spacing.md }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ flex: 1, padding: UI.spacing.md, backgroundColor: saving ? SEMANTIC.backgroundDisabled : SEMANTIC.ctaPrimary, color: saving ? SEMANTIC.textDisabled : SEMANTIC.ctaPrimaryText, border: 'none', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, cursor: saving ? 'not-allowed' : 'pointer' }}
            >
              {saving ? t('common.loading') : t('common.save')}
            </button>
            <button
              onClick={() => { setEditing(false); setError(''); }}
              disabled={saving}
              style={{ padding: `${UI.spacing.md} ${UI.spacing.xl}`, backgroundColor: 'transparent', color: SEMANTIC.textSecondary, border: `2px solid #555`, borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, cursor: 'pointer' }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MifidSettings;
